import { FileEntryMode, FileUpload as IrisFileUpload, Flex } from "@eworkit/iris";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";

import { PreviewCvFileEntry } from "@/components/Files/preview/PreviewCvFileEntry";
import { InProgressFileEntry } from "@/components/Files/upload/InProgressFileEntry";
import { buildDropzoneAccept, CVS_EXTENSIONS } from "@/consts/fileExtensions";
import { Attachment, TAttachmentPurpose } from "@/data/attachments";
import { withFormikField } from "@/formikComponents/withFormikField";
import { useFormats } from "@/hooks/Formats";

import { FileUploadStateItem } from "./fileUploadReducer";
import { useFileUpload } from "./useFileUpload";

interface CvFileUploadProps {
  cvs?: Attachment[];
  onChange?: (value: FileUploadStateItem<Attachment>[]) => void;
}

export const CvFileUpload: FC<CvFileUploadProps> = ({ onChange, cvs }) => {
  const { t } = useTranslation(["public", "common", "session"]);

  const { formatDate } = useFormats();

  const { state, addDropzoneCvFiles, deleteFileEntry, setCvAsDefault } = useFileUpload({
    attachments: cvs,
    onChange,
    attachmentType: TAttachmentPurpose.CV,
  });

  return (
    <Flex gap="md" flexDirection="column">
      <IrisFileUpload
        onDrop={addDropzoneCvFiles}
        accept={buildDropzoneAccept(CVS_EXTENSIONS)}
        maxSize={6}
        header={t("session:COMPONENTS.DROPPABLE_FILE_UPLOAD.DRAG_AND_DROP_FILES")}
        link={t("session:COMPONENTS.DROPPABLE_FILE_UPLOAD.CHOOSE_FROM_DRIVE")}
        multiple
      />

      <Flex gap="sm" flexDirection="column">
        {state.items.map((item, index) =>
          !item.attachment ? (
            <InProgressFileEntry
              key={index}
              attachment={item.file!}
              error={item.error && t(`common:VALIDATION_MESSAGES.${item.error}`, { size: 6 })}
              progress={item.progress}
              onCancel={deleteFileEntry}
              onDelete={deleteFileEntry}
            />
          ) : (
            <PreviewCvFileEntry
              key={index}
              attachment={item.attachment!}
              mode={FileEntryMode.EDIT}
              onDelete={deleteFileEntry}
              onSetDefault={setCvAsDefault}
              subtitle={
                item.file ? t("session:JOB_REQUESTS.APPLY.WIZARD.NEW") : formatDate(item.attachment.createdDate)
              }
            />
          ),
        )}
      </Flex>
    </Flex>
  );
};

export const FormikCvFileUpload = withFormikField(CvFileUpload);
